<template>
  <Card variant="tertiary" size="small" class="listing-status-controls">
    <template #header>
      <View wrap="wrap" justify="space-between" direction="row" :grow="1" gap="lg">
        <View gap="sm" class="title" wrap="wrap-reverse" align="center">
          <Text variant="h1">{{ propertyStore.property?.displayName }}</Text>
          <View gap="sm" align="center">
            <PropertyStatusLabel :status="propertyStatus" />
            <View gap="sm" v-if="propertyStatus === PropertyStatus.LISTED && listedAt">
              <Text variant="body-medium" color="secondary">∙</Text>
              <Text variant="body-medium" color="secondary">{{ listedAt }}</Text>
            </View>
          </View>
        </View>

        <View gap="sm" wrap="wrap">
          <Button class="add-new-lead-button-desktop" variant="outline" @click="addNewLead">
            <Icon name="user-plus-01" />
            <span>Add new lead</span>
          </Button>

          <Button class="add-new-lead-button-mobile" size="medium" variant="outline" @click="addNewLead">
            <Icon name="user-plus-01" />
            <span>Add new lead</span>
          </Button>

          <MenuButton buttonLabel="Manage listing" ref="menuButtonRef" class="menu-button">
            <MenuButtonItem
              v-if="isVisibleStartListingButton"
              title="Resume listing"
              icon="start"
              :manual-close="true"
              @click="startListing"
            />
            <MenuButtonItem
              v-else-if="isVisibleStopListingButton"
              title="Pause listing"
              variant="normal"
              icon="stop"
              :manual-close="true"
              @click="pauseListing"
            />
            <MenuButtonItem
              v-if="isVisibleMarkAsRentedOutButton"
              title="Mark as rented out"
              icon="check-circle"
              variant="normal"
              @click="markAsRentedOut"
            />
          </MenuButton>
        </View>
      </View>

      <PropertyMarkAsRentedOutModal ref="markAsRentedOutModalRef" />
      <PropertyPauseListingModal ref="pauseModalRef" />
      <PropertyListModal ref="resumeModalRef" />
      <AddNewLeadModal :propertyId="propertyId" ref="addNewLeadModalModalRef" />
    </template>
  </Card>
</template>

<script setup lang="ts">
import { useTimeAgo } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { Button, Card, Icon, MenuButton, MenuButtonItem, Text, View } from '@tallo/design-system';
import { PropertyStatusLabel, usePropertyStore } from '@tallo/investor/property';
import { AddNewLeadModal } from '@tallo/investor/renter';
import { PropertyStatus } from '@tallo/property';

import PropertyListModal from '../../components/property-list-modal.vue';
import PropertyMarkAsRentedOutModal from '../../components/property-mark-as-rented-out-modal.vue';
import PropertyPauseListingModal from '../../components/property-pause-listing-modal.vue';
import PropertyResumeListingModal from '../../components/property-resume-listing-modal.vue';

const route = useRoute();
const propertyStore = usePropertyStore();
const propertyId = ref(route.params.propertyId as string);
const menuButtonRef = ref<InstanceType<typeof MenuButton> | null>(null);
const markAsRentedOutModalRef = ref<InstanceType<typeof PropertyMarkAsRentedOutModal>>();
const pauseModalRef = ref<InstanceType<typeof PropertyPauseListingModal>>();
const resumeModalRef = ref<InstanceType<typeof PropertyResumeListingModal>>();
const addNewLeadModalModalRef = ref<InstanceType<typeof AddNewLeadModal>>();
const propertyStatus = computed(() => propertyStore.property?.status!);
const listedAt = computed(() =>
  propertyStore.property?.lastListedAt ? useTimeAgo(propertyStore.property?.lastListedAt) : null,
);

const isVisibleStartListingButton = computed(() => {
  return [
    PropertyStatus.READY_FOR_LISTING,
    PropertyStatus.UNLISTED,
    PropertyStatus.RENTED_OUT,
    PropertyStatus.DRAFT,
  ].includes(propertyStatus.value!);
});

const isVisibleStopListingButton = computed(() => {
  return [PropertyStatus.LISTING_IN_PROGRESS, PropertyStatus.LISTED].includes(propertyStatus.value!);
});

const isVisibleMarkAsRentedOutButton = computed(() => {
  return [
    PropertyStatus.READY_FOR_LISTING,
    PropertyStatus.LISTING_IN_PROGRESS,
    PropertyStatus.LISTED,
    PropertyStatus.UNLISTED,
  ].includes(propertyStatus.value!);
});

function startListing() {
  menuButtonRef.value?.close();
  resumeModalRef.value?.modalRef?.show();
}

function pauseListing() {
  menuButtonRef.value?.close();
  pauseModalRef.value?.modalRef?.show();
}

function markAsRentedOut() {
  menuButtonRef.value?.close();
  markAsRentedOutModalRef.value?.modalRef?.show();
}

function addNewLead() {
  addNewLeadModalModalRef.value?.modalRef?.show();
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.add-new-lead-button-desktop {
  @include mobile {
    display: none;
  }
}

.add-new-lead-button-mobile {
  @include tablet-start {
    display: none;
  }
}

.listing-status-controls {
  display: flex;
  justify-content: space-between;
}
</style>
